import {Hero as PrimerHero, useWindowSize, useTheme, Box, Grid, Tabs, Text, Link} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {AnimatePresence, motion} from 'motion/react'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {usePrefersReducedMotion} from '@github-ui/brand-core/hooks/usePrefersReducedMotion'
import {WebGLDither} from '@github-ui/brand-webgl'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {OS} from '@github-ui/get-os'
import {hpcAttrs} from '@github-ui/hpc'
import type {OSAwareDownloadAction} from '@github-ui/swp-core/components/OSAwareDownloadActionMenu'
import {useDetectedOS} from '@github-ui/swp-core/lib/hooks/use-detected-os'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import type {Visual, VisualResolved} from './../../Index.types'
import {resolveShowcaseOS} from './../../os-helpers'
import {ICONS, type IconName} from './../../svgs'

import {DecoratedDownloadActionMenu} from './../DecoratedDownloadActionMenu/DecoratedDownloadActionMenu'
import {MobileCta, type MobileCtaConfig} from './../MobileCta/MobileCta'
import {PlayButton} from './../PlayButton/PlayButton'

import Styles from './Hero.module.css'

export interface CarouselItem {
  label: string
  description: string
  visual: Visual
}

export interface HeroProps {
  label: string
  heading: string
  osBadges: Array<{
    iconName: Extract<IconName, 'windows' | 'apple' | 'linux'>
    label: string
  }>
  body: string
  cta1: {
    actions: readonly OSAwareDownloadAction[]
    menuLabel: string
  }
  cta2: {
    label: string
    href: string
  }
  carousel: CarouselItem[]
  a11y: {
    tabSelector: string
    playButton: string
  }
  initialOS?: OS
  mobile?: {
    cta: MobileCtaConfig
    heading: string
  }
}

const MotionBox = motion.create(Box)

export const Hero = ({label, heading, osBadges, body, cta1, cta2, carousel, a11y, initialOS, mobile}: HeroProps) => {
  const [activeCarouselIndex, setActiveCarouselIndex] = useState<number>(0)
  const [isVideoPaused, setIsVideoPaused] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const lastActiveIndexRef = useRef<number | null>(null)
  const detectedOS = useDetectedOS(initialOS)

  const {isLarge: isDesktopView} = useWindowSize()
  const {colorMode} = useTheme()
  const prefersReducedMotion = usePrefersReducedMotion()
  const resolvedOS = useMemo(() => resolveShowcaseOS(detectedOS), [detectedOS])

  const swapTransition = useMemo(
    () => (prefersReducedMotion ? {duration: 0} : {duration: 0.4, ease: [0.22, 1, 0.36, 1] as const}),
    [prefersReducedMotion],
  )

  const carouselItems = useMemo(() => {
    return carousel.map(item => {
      const visual: VisualResolved = {
        desktop: {
          ...item.visual.desktop,
          src: item.visual.desktop.src[resolvedOS][colorMode],
        },
        mobile: {
          ...item.visual.mobile,
          src: item.visual.mobile.src[resolvedOS][colorMode],
        },
      }

      return {
        ...item,
        visual,
      }
    })
  }, [carousel, colorMode, resolvedOS])

  const currentVisual = useMemo(
    () => carouselItems[activeCarouselIndex]?.visual[isDesktopView ? 'desktop' : 'mobile'],
    [activeCarouselIndex, carouselItems, isDesktopView],
  )

  const getVisual = useCallback(
    (visual: VisualResolved, index: number) => {
      const isActive = activeCarouselIndex === index
      return (
        <MotionBox
          key={`visual-${index}`}
          id={`hero-panel-${index}`}
          aria-labelledby={`hero-tab-${index}`}
          role="tabpanel"
          tabIndex={0}
          hidden={!isActive}
          className={Styles['Hero__visualPanel']}
          initial={false}
          animate={
            prefersReducedMotion ? {opacity: isActive ? 1 : 0} : {opacity: isActive ? 1 : 0, y: isActive ? 0 : 12}
          }
          transition={swapTransition}
        >
          {(visual.desktop.type === 'image' || visual.mobile.type === 'image') && (
            <picture
              key={`image-${index}-${resolvedOS}-${colorMode}`}
              className={clsx(Styles['Hero__visual'], Styles['Hero__visual--image'])}
            >
              {visual.mobile.type === 'image' && <source srcSet={visual.mobile.src} media="(max-width: 543px)" />}
              {visual.desktop.type === 'image' && <source srcSet={visual.desktop.src} media="(min-width: 544px)" />}
              <img
                src={visual.desktop.type === 'image' ? visual.desktop.src : visual.mobile.src}
                alt={isDesktopView || visual.mobile.type !== 'image' ? visual.desktop.alt : visual.mobile.alt}
              />
            </picture>
          )}

          {visual.desktop.type === 'video' && visual.mobile.type === 'video' && (
            <video
              key={`video-${index}-${resolvedOS}-${colorMode}`}
              className={Styles['Hero__visual']}
              autoPlay
              loop
              muted
              playsInline
              poster={visual.desktop.coverImageSrc}
              aria-label={isDesktopView ? visual.desktop.alt : visual.mobile.alt}
            >
              <source src={visual.mobile.src} media="(max-width: 543px)" />
              <source src={visual.desktop.src} media="(min-width: 544px)" />
            </video>
          )}
        </MotionBox>
      )
    },
    [activeCarouselIndex, colorMode, isDesktopView, prefersReducedMotion, resolvedOS, swapTransition],
  )

  useEffect(() => {
    if (!wrapperRef.current) return
    const visuals = wrapperRef.current.querySelectorAll(`.${Styles['Hero__visual']}`)
    const indexChanged = lastActiveIndexRef.current !== activeCarouselIndex
    lastActiveIndexRef.current = activeCarouselIndex

    for (const [index, visual] of visuals.entries()) {
      if (!(visual instanceof HTMLVideoElement)) continue

      if (index !== activeCarouselIndex) {
        try {
          visual.pause()
        } catch {
          // Fail silently
        }
        continue
      }

      if (indexChanged) visual.currentTime = 0
      if (isVideoPaused || prefersReducedMotion) visual.pause()
      else
        void (async () => {
          try {
            await visual.play()
          } catch {
            /* autoplay may be blocked */
          }
        })()
    }
  }, [activeCarouselIndex, isVideoPaused, prefersReducedMotion])

  const onMountSyncDither = useEffectEvent(() => {
    const hasVideoVisuals = carouselItems.some(
      item => item.visual[isDesktopView ? 'desktop' : 'mobile'].type === 'video',
    )
    if (!wrapperRef.current || !hasVideoVisuals) return

    const wrapper = wrapperRef.current
    const imageContainer: HTMLElement | null = wrapper.querySelector(`.${Styles['Hero__imageContainer']}`)
    const ditherSelector = `.${Styles['Hero__imageDither']}`
    if (!imageContainer) return

    const syncSize = () => {
      const imageDither: HTMLElement | null = wrapper.querySelector(ditherSelector)
      if (!imageDither) return
      const {width, height} = imageContainer.getBoundingClientRect()
      imageDither.style.width = `${width}px`
      imageDither.style.height = `${height}px`
    }

    const resizeObserver = new ResizeObserver(syncSize)
    resizeObserver.observe(imageContainer)

    // The dither element only enters the DOM once `WebGLDither`'s lazy chunk resolves.
    const mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement && (node.matches(ditherSelector) || node.querySelector(ditherSelector))) {
            syncSize()
            return
          }
        }
      }
    })

    mutationObserver.observe(wrapper, {childList: true, subtree: true})
    syncSize()

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  })

  useEffect(() => {
    return onMountSyncDither()
  }, [])

  return (
    <div ref={wrapperRef} className={Styles['Hero']}>
      <PrimerHero
        {...hpcAttrs()}
        className={Styles['Hero__primerHero']}
        variant="gridline-expressive"
        enableAnimation={!isFeatureEnabled('site_landing_pages_disable_hero_animations')}
        trailingComponent={() => (
          <div className={clsx(Styles['Hero__ctas'], mobile && Styles['Hero__ctas--mobile'])}>
            {mobile ? (
              <>
                <MobileCta block config={mobile.cta} location="hero" />

                <Link
                  href={cta2.href}
                  variant="accent"
                  arrowDirection="none"
                  {...getAnalyticsEvent({action: cta2.label, tag: 'a', context: 'cta', location: 'hero'})}
                >
                  {cta2.label}
                </Link>
              </>
            ) : (
              <>
                <DecoratedDownloadActionMenu actions={cta1.actions} menuLabel={cta1.menuLabel} location="hero" />

                <Link
                  href={cta2.href}
                  variant="accent"
                  arrowDirection="none"
                  {...getAnalyticsEvent({action: cta2.label, tag: 'a', context: 'cta', location: 'hero'})}
                >
                  {cta2.label}
                </Link>
              </>
            )}
          </div>
        )}
      >
        <PrimerHero.Label>{label}</PrimerHero.Label>
        <PrimerHero.Heading>{mobile?.heading ?? heading}</PrimerHero.Heading>

        <PrimerHero.Description>
          <span role="list" className={Styles['Hero__osBadges']}>
            {osBadges.map(badge => (
              <span
                role="listitem"
                key={badge.label}
                className={Styles['Hero__osBadges-item']}
                aria-label={badge.label}
              >
                {ICONS[badge.iconName]}
              </span>
            ))}
          </span>

          {body}
        </PrimerHero.Description>
      </PrimerHero>

      <div className={Styles['Hero__content']}>
        <Box className={Styles['Hero__gridFrame']}>
          <Box className={clsx(Styles['Hero__gridContent'], Styles['Hero__gridContentWide'])}>
            <Grid columnGap="none" rowGap="none" enableGutters={false}>
              <Grid.Column span={12} className={Styles['Hero__gridColumn']}>
                <div className={Styles['Hero__visualsContent']}>
                  {carouselItems.map((item, index) => getVisual(item.visual, index))}

                  <div
                    className={clsx(
                      Styles['Hero__playButtonWrapper'],
                      currentVisual?.type !== 'video' && Styles['Hero__playButtonWrapper--hidden'],
                    )}
                    inert={currentVisual?.type !== 'video' ? true : undefined}
                  >
                    <PlayButton isPaused={isVideoPaused} onToggle={setIsVideoPaused} a11y={{label: a11y.playButton}} />
                  </div>

                  <WebGLDither
                    className={Styles['Hero__ditherBg']}
                    params={{color: 'pearl', type: 'wave', fadeIn: currentVisual?.type === 'image', dotOpacity: 0.2}}
                  />
                </div>

                <div className={Styles['Hero__tabs']}>
                  <Tabs
                    className={Styles['Hero__tabsWrapper']}
                    onChange={tabIndex => {
                      setActiveCarouselIndex(+tabIndex)
                      setIsVideoPaused(false)
                    }}
                    aria-label={a11y.tabSelector}
                  >
                    {carouselItems.map((item, index) => (
                      <Tabs.Item
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={`tab-${index}`}
                        id={`hero-tab-${index}`}
                        aria-controls={`hero-panel-${index}`}
                        value={index}
                      >
                        {item.label}
                      </Tabs.Item>
                    ))}
                  </Tabs>

                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={activeCarouselIndex}
                      initial={prefersReducedMotion ? false : {opacity: 0, y: 12}}
                      animate={{opacity: 1, y: 0}}
                      exit={prefersReducedMotion ? {opacity: 1} : {opacity: 0, y: -12}}
                      transition={swapTransition}
                    >
                      <Text variant="muted">{carouselItems[activeCarouselIndex]?.description}</Text>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </Grid.Column>
            </Grid>
          </Box>
        </Box>
      </div>
    </div>
  )
}
